import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Phone } from "lucide-react";
import { MaskLine } from "./Reveal";
import { IMAGES, CONTACT } from "../../lib/data";
import { scrollToId } from "../../lib/scroll";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const go = (href) => scrollToId(href);

  return (
    <section id="top" ref={ref} className="relative min-h-screen bg-paper pt-28 sm:pt-32 overflow-hidden" data-testid="hero-section">
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-12 gap-6 items-end">
          {/* Headline */}
          <div className="col-span-12 lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="h-px w-10 bg-redx" />
              <span className="font-oswald uppercase text-xs tracking-[0.3em] text-redx">
                Electrolux Professional Sertifisert Partner
              </span>
            </motion.div>

            <h1 className="font-oswald font-bold uppercase tracking-tighter leading-[0.82] text-[16vw] sm:text-[12vw] lg:text-[9vw] text-ink">
              <MaskLine load delay={0.35}>Alt til</MaskLine>
              <MaskLine load delay={0.45}>stor<span className="text-redx">kjøkken</span></MaskLine>
              <MaskLine load delay={0.55}>& vaskeri</MaskLine>
            </h1>
          </div>

          {/* Side copy */}
          <div className="col-span-12 lg:col-span-5 lg:pb-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="text-lg sm:text-xl text-ink/70 leading-relaxed max-w-md">
                Vi leverer utstyr, prosjektering og service til storkjøkken, vaskeri, skyllerom og institusjon — med erfarne servicefolk i hele Midt-Norge.
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <button
                  onClick={() => go("#kontakt")}
                  data-testid="hero-contact-button"
                  className="group flex items-center gap-2 bg-redx text-white px-7 py-4 font-oswald uppercase tracking-widest text-sm hover:bg-ink transition-colors duration-200"
                >
                  Kontakt oss <ArrowDownRight size={18} className="group-hover:translate-x-1 group-hover:translate-y-1 transition-transform" />
                </button>
                <button
                  onClick={() => go("#tjenester")}
                  data-testid="hero-services-button"
                  className="border border-ink text-ink px-7 py-4 font-oswald uppercase tracking-widest text-sm hover:bg-ink hover:text-white transition-colors duration-200"
                >
                  Se tjenester
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Clipped spotlight image */}
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ delay: 0.6, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-12 sm:mt-16 h-[46vh] sm:h-[62vh] overflow-hidden"
          data-testid="hero-image"
        >
          <motion.img
            src={IMAGES.hero}
            alt="Profesjonelt storkjøkken i rustfritt stål"
            style={{ y: imgY, scale: imgScale }}
            className="absolute inset-0 w-full h-[120%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 flex flex-wrap items-end justify-between gap-4 p-6 sm:p-10">
            <span className="font-oswald uppercase text-white text-sm tracking-[0.3em]">Midt-Norge</span>
            <a href={`tel:+47${CONTACT.phoneRaw}`} className="inline-flex items-center gap-2 font-oswald uppercase text-white text-2xl sm:text-4xl tracking-tight hover:text-redx transition-colors" data-testid="hero-phone">
              <Phone className="w-[0.8em] h-[0.8em]" /> {CONTACT.phone}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
